import './uploader.css';

import * as React from 'react';
import { type FileRejection } from 'react-dropzone';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { DropField } from './DropField';
import { FileUploadDetails } from './FileUploadDetails';
import { type UploadItem, type ValidationError } from './types';
import {
    buildAccept,
    formatAcceptedFileTypes,
    formatBytes,
    validateFile,
} from './utils';

export interface UploaderProps {
    /** File extensions (with or without dot) or MIME types to accept. */
    acceptedFileTypes?: string[];
    /** Maximum allowed size, in bytes. */
    maxFileSize?: number;
    /** Whether more than one file can be selected. Defaults to `false`. */
    isMultiple?: boolean;
    /**
     * When set, the drop area renders in its error variant and this
     * message replaces any internally generated validation message.
     */
    errorText?: string;
    /** Controlled list of uploads displayed below the drop area. */
    uploads?: UploadItem[];
    /** Disables both drop and browse interactions. */
    isDisabled?: boolean;
    /** Called with the files that passed validation. */
    onFilesAdded?: (files: File[]) => void;
    /** Called when the user removes a file via the X button. */
    onFileRemove?: (item: UploadItem) => void;
    /**
     * Called when the user clicks the retry button on a failed upload. The
     * retry button is only rendered when this callback is provided and the
     * item's status is `'error'`.
     */
    onFileRetry?: (item: UploadItem) => void;
    /** Called with the validation errors that caused files to be rejected. */
    onValidationError?: (errors: ValidationError[]) => void;
    className?: string;
}

const Uploader = React.forwardRef<HTMLDivElement, UploaderProps>(
    ({
        acceptedFileTypes,
        maxFileSize,
        isMultiple = false,
        errorText,
        uploads = [],
        isDisabled = false,
        onFilesAdded,
        onFileRemove,
        onFileRetry,
        onValidationError,
        className,
    }, ref) => {
        const { t } = useTranslation();
        const reactId = React.useId();
        const helperTextId = `${reactId}-helper`;
        const errorMessageId = `${reactId}-error`;

        const [internalError, setInternalError] = React.useState<string | undefined>();

        const accept = React.useMemo(
            () => buildAccept(acceptedFileTypes),
            [acceptedFileTypes],
        );

        const computedHelperText = React.useMemo(() => {
            const formats = formatAcceptedFileTypes(acceptedFileTypes);
            const maxSize = typeof maxFileSize === 'number' ? formatBytes(maxFileSize) : '';
            if (!formats && !maxSize) {
                return undefined;
            }
            return t('UPLOADER.SUPPORTED_FORMATS_AND_SIZE', {
                formats: formats || '*',
                maxSize: maxSize || '∞',
            });
        }, [acceptedFileTypes, maxFileSize, t]);

        const handleFilesAdded = React.useCallback(
            (files: File[]) => {
                const errors: ValidationError[] = [];
                const accepted: File[] = [];

                for (const file of files) {
                    const error = validateFile(file, { acceptedFileTypes, maxFileSize });
                    if (error) {
                        errors.push(error);
                    } else {
                        accepted.push(file);
                    }
                }

                if (errors.length > 0) {
                    setInternalError(translateValidationError(errors[0], { acceptedFileTypes, maxFileSize, t }));
                    onValidationError?.(errors);
                } else {
                    setInternalError(undefined);
                }

                if (accepted.length > 0) {
                    onFilesAdded?.(accepted);
                }
            },
            [acceptedFileTypes, maxFileSize, onFilesAdded, onValidationError, t],
        );

        const handleFilesRejected = React.useCallback(
            (rejections: FileRejection[]) => {
                if (rejections.length === 0) {
                    return;
                }
                const errors: ValidationError[] = rejections.map((rejection) => {
                    const first = rejection.errors[0];
                    const code = (first?.code as ValidationError['code']) ?? 'file-invalid-type';
                    return { code, file: rejection.file, message: first?.message };
                });
                setInternalError(translateValidationError(errors[0], { acceptedFileTypes, maxFileSize, t }));
                onValidationError?.(errors);
            },
            [acceptedFileTypes, maxFileSize, onValidationError, t],
        );

        const effectiveError = errorText ?? internalError;

        return (
            <div
                ref={ref}
                className={cn(
                    'tw-flex tw-flex-col tw-gap-6 tw-w-full',
                    'tw-items-start tw-justify-center tw-p-5 lg:!tw-p-8',
                    className,
                )}
                data-testid="uploader"
            >
                <DropField
                    accept={accept}
                    maxFileSize={maxFileSize}
                    isMultiple={isMultiple}
                    disabled={isDisabled}
                    helperText={computedHelperText}
                    errorMessage={effectiveError}
                    helperTextId={helperTextId}
                    errorMessageId={errorMessageId}
                    onFilesAdded={handleFilesAdded}
                    onFilesRejected={handleFilesRejected}
                />

                <FileUploadDetails
                    uploads={uploads}
                    isMultiple={isMultiple}
                    onRemove={onFileRemove}
                    onRetry={onFileRetry}
                />
            </div>
        );
    },
);

Uploader.displayName = 'Uploader';

function translateValidationError(
    error: ValidationError,
    { acceptedFileTypes, maxFileSize, t }: {
        acceptedFileTypes?: string[];
        maxFileSize?: number;
        t: (key: string, values?: Record<string, string | number>) => string;
    },
): string {
    if (error.code === 'file-too-large' && typeof maxFileSize === 'number') {
        return t('UPLOADER.FILE_TOO_LARGE', { size: formatBytes(maxFileSize) });
    }
    if (error.code === 'file-invalid-type') {
        return t('UPLOADER.FILE_TYPE_INVALID', {
            types: formatAcceptedFileTypes(acceptedFileTypes) || '*',
        });
    }
    return error.message ?? t('UPLOADER.FILE_TYPE_INVALID', {
        types: formatAcceptedFileTypes(acceptedFileTypes) || '*',
    });
}

export { Uploader };
